import React, { useMemo, useEffect } from 'react'
import CartReviewTile, { LoyaltyPointsEarningConfiguration } from 'components/Pages/CartReviewPage/CartReviewTile'
import CartReviewPriceStack from 'components/Pages/CartReviewPage/CartReviewPriceStack'
import CartReviewBanner from 'components/Pages/CartReviewPage/CartReviewBanner'
import { getBannerTypeVariant } from 'components/Pages/CartReviewPage/CartReviewUtils'
import { getExperienceOfferPageURL } from 'lib/offer/offerPageURL'
import ExperienceCancellationText from 'components/Experiences/ExperienceCancellationPolicy/ExperienceCancellationText'
import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import moment from 'moment'
import CartReviewTileImageContent from 'components/Pages/CartReviewPage/CartReviewTileImageContent'
import { getUserCartItemDisplayTotals } from 'lib/userCart/userCartTotals'
import CondensedCartReviewTile from 'components/Pages/CartReviewPage/CondensedCartReviewTile'
import CondensedCartReviewTileContent from 'components/Pages/CartReviewPage/CondensedCartReviewTileContent'
import CartReviewTileContent from 'components/Pages/CartReviewPage/CartReviewTileContent'
import LoadingCartReviewTileSkeleton from 'components/Pages/CartReviewPage/LoadingCartReviewTileSkeleton'
import CartReviewTileActions from 'components/Pages/CartReviewPage/CartReviewTileActions'
import { mapExperienceCartItemToSummaryViews } from 'checkout/lib/utils/experiences/view'
import useExperienceAvailableTimes from 'hooks/ExperiencePage/useExperienceAvailableTimes'
import useExperience from 'hooks/Experiences/useExperience'
import { isNonNullable, sum } from 'lib/array/arrayUtils'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { dateIsBeforeOrSame } from 'lib/datetime/dateUtils'
import { useSelector } from '@xstate/react'
import { MultiCartContext } from 'machines/cart/MultiCartMachineProvider'
import UndoDeletedItemsTile from '../UndoDeletedItemsTile'
import { MultiCartReviewMachineContext } from '../MultiCartReviewMachineContext'
import MultiCartParticipantLabel from './MultiCartParticipantLabel'
import SharedCartComments from '../Comments/SharedCartComments'
import { isLEAdmin } from 'selectors/accountSelectors'

interface Props {
  multiCartItem: App.MultiCartExperienceItem
  className?: string
  hideTitleAndDates?: boolean
  hideAvailabilityDetails?: boolean
  disableOverlay?: boolean
  saveForLaterIfUnavailable?: boolean
  tileVariant?: 'default' | 'condensed'
}

function ExperienceMultiCartReviewTile({
  multiCartItem,
  className,
  hideTitleAndDates = false,
  hideAvailabilityDetails = false,
  disableOverlay = false,
  saveForLaterIfUnavailable = true,
  tileVariant = 'default',
}: Props) {
  // ============================================================================
  // HOOKS AND CONTEXT
  // ============================================================================
  const item = multiCartItem.item as App.Checkout.ExperienceItem
  const dispatch = useAppDispatch()
  const multiCartActorRef = MultiCartContext.useActorRef()
  const [experience, experienceLoading, experienceError] = useExperience(multiCartItem.experienceId)
  const multiCartReviewActorRef = MultiCartReviewMachineContext.useActorRef()
  // State selectors
  const activeCartId = useSelector(multiCartActorRef, (snapshot) => snapshot.context.activeCartId)
  const cartSummary = useSelector(multiCartActorRef, (snapshot) => snapshot.context.cartSummary)
  const isAdmin = useAppSelector(isLEAdmin)
  const isSharedCart = cartSummary.status === 'loaded' && activeCartId && cartSummary.data[activeCartId]?.type !== 'default'

  // ============================================================================
  // STATE CALCULATIONS
  // ============================================================================
  // For multi-cart items, we get the state from multiCartItem instead of useUserCartItemState
  const isDeleted = multiCartItem.state === 'deleted'
  const saveForLater = multiCartItem.state === 'save_for_later'
  const isSoldOut = !!experience && experience.status !== 'ONLINE'
  const isPast = dateIsBeforeOrSame(new Date(multiCartItem.date!), new Date())
  const isBookable = experience?.status === 'ONLINE' && !experienceError && !isPast

  // ============================================================================
  // DATA FETCHING EFFECTS
  // ============================================================================
  const [timeSlots, slotsLoading, slotsError] = useExperienceAvailableTimes(experience, {
    date: multiCartItem.date,
    isBuyNowBookLater: multiCartItem.isBuyNowBookLater,
    isGift: multiCartItem.isGift,
    pickupPointId: multiCartItem.pickupPointId,
    redemptionLocationId: multiCartItem.redemptionLocationId,
    ticketModesMap: multiCartItem.ticketModesMap,
  })

  // ============================================================================
  // AUTO SAVE FOR LATER EFFECT
  // ============================================================================
  useEffect(() => {
    if (experience && !isBookable && !saveForLater && !experienceLoading && !isDeleted && saveForLaterIfUnavailable) {
      multiCartReviewActorRef.send({
        type: 'update save items for later',
        cartId: activeCartId!,
        itemIds: [multiCartItem.id],
        saveForLater: !saveForLater,
        skipNotification: true,
      })
    }
  }, [isBookable, saveForLater, experienceLoading, dispatch, item.itemId, experience, isDeleted, saveForLaterIfUnavailable, activeCartId, multiCartReviewActorRef, multiCartItem.id])

  // ============================================================================
  // COMPUTED VALUES AND MEMOIZED DATA
  // ============================================================================
  const itemTotals: App.Checkout.ItemViewTotals | undefined = useMemo(() => {
    const hasTickets = timeSlots?.some(slot => slot.tickets.length)

    if (experience && timeSlots && hasTickets) {
      const timeSlotTickets = timeSlots.flatMap(slot => slot.tickets.map(ticket => ({ ...ticket, time: slot.time })))
      const requiresTime = experience.bookingType === 'CALENDAR-TIMESLOTS' && !multiCartItem.isBuyNowBookLater && !multiCartItem.isGift

      const ticketViews = item.tickets.flatMap(ticketItem => {
        const ticket = timeSlotTickets.find(t => {
          return (
            t.id === ticketItem.ticketId &&
            t.date === ticketItem.date &&
            (!requiresTime || t.time == multiCartItem.time)
          )
        })
        return mapExperienceCartItemToSummaryViews(ticketItem, ticket)
      }).filter(isNonNullable)

      const price = sum(ticketViews, tv => tv.price)
      const value = sum(ticketViews, tv => tv.value)
      const taxesAndFees = sum(ticketViews, tv => tv.taxesAndFees)

      const appPriceTickets = ticketViews.filter(tv => tv.discounts.app.amount > 0)
      const appDiscountAmount = sum(appPriceTickets, tv => tv.discounts.app.amount)
      const appDiscountPercentage = appDiscountAmount > 0 ? sum(appPriceTickets, tv => tv.discounts.app.percentage) / appPriceTickets.length : 0

      return {
        price,
        memberPrice: 0,
        value,
        mobileAppDiscount: {
          amount: appDiscountAmount,
          percentage: appDiscountPercentage,
        },
        taxesAndFees,
        surcharge: 0,
        memberValue: 0,
        extraGuestSurcharge: 0,
      }
    }
  }, [experience, item, timeSlots, multiCartItem.isBuyNowBookLater, multiCartItem.isGift, multiCartItem.time])

  // Loading and error states
  const loading = experienceLoading || !!slotsLoading
  const error = experienceError ?? slotsError

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================
  const handleSaveForLater = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    multiCartReviewActorRef.send({
      type: 'update save items for later',
      cartId: activeCartId!,
      itemIds: [multiCartItem.id],
      saveForLater: !saveForLater,
    })
  }

  const handleDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    if (!activeCartId) return

    multiCartActorRef.send({
      type: 'delete items from given cart',
      cartId: activeCartId,
      itemIds: [multiCartItem.id],
      currentStates: {
        [multiCartItem.id]: multiCartItem.state,
      },
    })
  }

  const {
    publicTotal,
    memberTotal,
  } = useMemo(() => getUserCartItemDisplayTotals(itemTotals), [itemTotals])

  const formattedDateRange = useMemo(() => {
    if (!multiCartItem.date) return ''
    const formattedDate = moment(multiCartItem.date).format(DMY_CASUAL_FORMAT)
    if (!experience?.hasTimeslots) return formattedDate
    return multiCartItem.time ? `${formattedDate} · ${multiCartItem.time}` : formattedDate
  }, [multiCartItem.date, multiCartItem.time, experience?.hasTimeslots])

  const formattedTickets = useMemo(() => {
    // Use item.tickets instead of multiCartItem.tickets due to type definition differences
    return item.tickets
      .filter(ticket => ticket.count > 0)
      .map(ticket => `${ticket.count} × ${ticket.name}`)
  }, [item.tickets])

  const experienceURL = useMemo(() =>
    getExperienceOfferPageURL(experience),
  [experience],
  )

  const loyaltyPointsEarningConfiguration = useMemo<LoyaltyPointsEarningConfiguration>(() => {
    return {
      calculationRequests: [
        {
          price: publicTotal,
          loyaltyProductType: 'experience',
        },
      ],
      calculationType: 'exact',
    }
  }, [publicTotal])

  // ============================================================================
  // UI STATE CALCULATIONS
  // ============================================================================
  const bannerTypeVariant = getBannerTypeVariant(isPast, isSoldOut, false, !!error)
  const cancellationInfo = experience?.copy.cancellationInfo
  const image = experience?.images[0]

  // ============================================================================
  // EARLY RETURNS
  // ============================================================================

  if (loading) {
    return <LoadingCartReviewTileSkeleton />
  }

  if (isDeleted) {
    return <UndoDeletedItemsTile
      title={experience?.name || ''}
      onUndoClick={() => {
        multiCartActorRef.send({
          itemIds: [multiCartItem.id],
          type: 'undo delete',
        })
      }}
    />
  }

  // ============================================================================
  // RENDER
  // ============================================================================

  if (tileVariant === 'condensed') {
    return (
      <CondensedCartReviewTile
        to={experienceURL}
        banner={<CartReviewBanner
          variant={bannerTypeVariant}
        />}
        onDelete={handleDelete}
        content={<CondensedCartReviewTileContent
          title={experience?.name || ''}
          dates={formattedDateRange}
          packageTitle={formattedTickets}
        />}
        imageElement={image ? <CartReviewTileImageContent image={image} /> : undefined}
        price={<CartReviewPriceStack
          price={publicTotal}
          memberPrice={memberTotal}
          value={itemTotals?.value ?? 0}
          luxPlusOfferConfig={experience?.luxPlus}
        />}
      />
    )
  }

  const commentsFooter = activeCartId && isSharedCart ? (
    <SharedCartComments
      multiCartId={activeCartId}
      itemIds={[multiCartItem.id]}
      isAdmin={isAdmin}
      noBorder
    />
  ) : undefined

  return (
    <CartReviewTile
      to={experienceURL}
      imageElement={image ? <CartReviewTileImageContent image={image} /> : undefined}
      content={<CartReviewTileContent
        title={experience?.name || ''}
        dates={formattedDateRange}
        hideTitleAndDates={hideTitleAndDates}
        packageTitle={formattedTickets}
        loyaltyPointsEarningConfiguration={loyaltyPointsEarningConfiguration}
        cancellationPolicy={cancellationInfo && (
          <ExperienceCancellationText
            cancellationInfo={cancellationInfo}
            date={multiCartItem.date}
            textSize="medium"
          />
        )}
        label={<MultiCartParticipantLabel item={multiCartItem} />}
      />}
      price={<CartReviewPriceStack
        price={publicTotal}
        memberPrice={memberTotal}
        value={itemTotals?.value ?? 0}
        luxPlusOfferConfig={experience?.luxPlus}
      />}
      banner={<CartReviewBanner
        variant={bannerTypeVariant}
        to={experienceURL}
      />}
      footer={commentsFooter}
      isBookable={isBookable}
      saveForLater={saveForLater}
      actions={<CartReviewTileActions
        isBookable={isBookable}
        saveForLater={saveForLater}
        onSaveForLater={handleSaveForLater}
        onDelete={handleDelete}
      />}
      className={className}
      hideAvailabilityDetails={hideAvailabilityDetails}
      disableOverlay={disableOverlay}
    />
  )
}

export default ExperienceMultiCartReviewTile
